/**
 * Render comments feature module.
 *
 * Purpose: Public API entry point for rendering comment annotations.
 * Orchestrates state updates, subscriptions, and delegates to commentRenderer.
 *
 * Key responsibilities:
 * - Update state with new annotations
 * - Subscribe to selected annotations from Velt
 * - Delegate rendering logic to commentRenderer
 *
 * Dependencies:
 * - adapters/velt.ts (for Velt SDK subscription)
 * - adapters/host/doc.ts (for editor setup)
 * - core/state.ts (for state management)
 * - core/registry.ts (for editor ID extraction)
 * - features/commentRenderer.ts (for rendering logic)
 * - types/velt.ts (for CommentAnnotation)
 *
 * IMPORTANT: This module MUST NOT import editor or Velt types directly.
 * All SDK access goes through adapters.
 */
import { ensureEditorSetup } from '@/adapters/host/doc';
import { ensureSelectedCommentsSubscription, subscribeToSelectedAnnotations } from '@/adapters/velt';
import { Constants } from '@/constants/common';
import { setSelectedAnnotations, updateAnnotations } from '@/core/state';
import { cleanupRenderer, updateComments, updateSelection } from '@/features/commentRenderer';
import { Console } from '@/utils/console';
/**
 * Subscription map to track subscriptions per editor
 */
const subscriptionMap = new Map();
/**
 * Renders comment annotations as marks in the editor.
 *
 * This function renders comment annotations as visual marks in the editor.
 * It filters annotations for the specific editor and applies marks accordingly.
 *
 * @param request - Object containing editor, editorId (optional), and commentAnnotations (optional)
 *
 * @example
 * ```typescript
 * // Simple usage
 * renderComments({ editor });
 *
 * // With editor ID
 * renderComments({ editorId: 'my-editor', editor });
 *
 * // With annotations
 * renderComments({
 *   editorId: 'my-editor',
 *   editor,
 *   commentAnnotations: [
 *     {
 *       annotationId: 'ann-123',
 *       context: {
 *         textEditorConfig: {
 *           text: 'Hello world',
 *           occurrence: 1,
 *           editorId: 'my-editor'
 *         }
 *       }
 *     }
 *   ]
 * });
 * ```
 *
 * @remarks
 * - Only annotations with matching editorId are rendered
 * - Automatically subscribes to selected annotations changes
 * - Filters out terminal/resolved comments unless they're selected
 * - Removes marks when comments become resolved
 * - Re-applies marks when resolved comments are selected
 */
export const renderComments = ({ editor, editorId, commentAnnotations, }) => {
    const logPrefix = '[renderComments]';
    try {
        if (!editor) {
            Console.warn(`${logPrefix} No editor provided`);
            return;
        }
        // Validate commentAnnotations silently - skip if not available or not an array
        if (!commentAnnotations || !Array.isArray(commentAnnotations)) {
            commentAnnotations = [];
        }
        // Step 0: Ensure editor is set up (registered and document listeners configured)
        const { editorId: finalEditorId } = ensureEditorSetup(editorId, editor);
        // Deep clone annotations to avoid mutations
        const clonedAnnotations = JSON.parse(JSON.stringify(commentAnnotations));
        // Filter annotations for this specific editor
        // - If finalEditorId is DEFAULT_EDITOR_ID, match annotations without editorId OR with editorId === DEFAULT_EDITOR_ID
        // - Otherwise, match annotations with exact editorId match
        const editorAnnotations = clonedAnnotations.filter((annotation) => {
            var _a;
            if (!((_a = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig)) {
                return false;
            }
            const annotationEditorId = annotation.context.textEditorConfig.editorId;
            // Default editor matches: no editorId OR explicit DEFAULT_EDITOR_ID
            if (finalEditorId === Constants.DEFAULT_EDITOR_ID) {
                return annotationEditorId === undefined || annotationEditorId === Constants.DEFAULT_EDITOR_ID;
            }
            // For user-provided editorIds, require exact match
            return annotationEditorId === finalEditorId;
        });
        // Step 1: Update state with filtered annotations (stores both AnnotationData and CommentAnnotation[])
        updateAnnotations(finalEditorId, editorAnnotations);
        // Step 2: Set up subscription to selected annotations (singleton pattern)
        if (!subscriptionMap.has(finalEditorId)) {
            // Capture finalEditorId in const to ensure type safety in closure
            const editorIdForSubscription = finalEditorId;
            const unsubscribe = subscribeToSelectedAnnotations((selectedIds) => {
                // Update selected annotations in state
                setSelectedAnnotations(editorIdForSubscription, selectedIds);
                // Trigger re-rendering with new selection
                updateSelection(editorIdForSubscription, selectedIds);
            }, editorIdForSubscription);
            subscriptionMap.set(finalEditorId, unsubscribe);
        }
        // Step 2.5: Ensure the selected comments subscription is active
        // This retries the subscription if it failed earlier (e.g., commentElement wasn't ready)
        ensureSelectedCommentsSubscription();
        // Step 3: Delegate to commentRenderer to handle filtering, comparison, removal, and re-application
        updateComments(finalEditorId, editor, editorAnnotations);
    }
    catch (error) {
        Console.catch(`${logPrefix} Error rendering comments:`, error);
    }
};
/**
 * Cleans up subscriptions and renderer state for an editor.
 * Should be called when editor is destroyed.
 *
 * @param editorId Unique identifier for the editor
 */
export const cleanupRenderComments = (editorId) => {
    // Clean up subscription
    const unsubscribe = subscriptionMap.get(editorId);
    if (unsubscribe) {
        unsubscribe();
        subscriptionMap.delete(editorId);
    }
    // Clean up renderer store
    cleanupRenderer(editorId);
};
